// src/components/XpModal.jsx
import React from 'react';
import { X, Check, Zap, Heart, Backpack, Flame, Lock, Gift } from 'lucide-react';
import { playSound } from '../utils/sounds';
import Mascot from './Mascot';
import { SKINS_DATA as SHOP_ITEMS } from '../data/skins';
import { getMascotDialogue } from '../data/mascotDialogues';

export default function XpModal({
  userProgress,
  gameData,
  onClose,
  onBuySkin,
  onEquipSkin,
  onBuyLife,
  onBuyChest,
  onInsufficientXp,  // ✅ Nueva prop
  isLegendary = false 
}) {
  // --- LÓGICA DE NIVELES (Cada 500 XP) ---
  const userXp = Number(userProgress.xp || 0);
  const currentLevel = Math.floor(userXp / 500) + 1;
  const progressInLevel = userXp % 500;
  const percentage = Math.min((progressInLevel / 500) * 100, 100);
  const nextLevelXp = currentLevel * 500;

  const currentSkin = userProgress.currentSkin || 'bribark';
  const isBackpackFull = gameData.lives >= gameData.BACKPACK_CAP;

  // Costos fijos
  const LIFE_COST = 50;
  const CHEST_COST = 500;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-10 duration-500">
        
        {/* HEADER: NIVEL Y PROGRESO */}
        <div className="bg-indigo-600 p-6 relative text-white text-center shrink-0">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/20 rounded-full hover:bg-white/40 transition-colors z-10">
            <X size={20} />
          </button>

          <div className="flex justify-between items-center mb-4 text-left">
            <div>
              <span className="text-indigo-300 text-[10px] font-black uppercase tracking-widest">NIVEL {currentLevel}</span>
              <p className="text-2xl font-black">{userXp} XP</p>
            </div>
            
            <div className="flex gap-2">
              <div className="bg-indigo-800/50 p-2 rounded-xl flex flex-col items-center min-w-[60px]">
                <Flame size={18} className={gameData.streak > 0 ? "text-orange-400 fill-orange-400" : "text-slate-400"} />
                <span className="text-xs font-bold mt-1">{gameData.streak} Racha</span>
              </div>
              
              <div className="bg-indigo-800/50 p-2 rounded-xl flex flex-col items-center min-w-[60px]">
                <div className="relative">
                  <Backpack size={18} className={isBackpackFull ? "text-green-400" : "text-white"} />
                  <div className="absolute -bottom-1 -right-1 bg-rose-500 text-[8px] px-1 rounded-full font-bold">
                    {gameData.lives}
                  </div>
                </div>
                <span className="text-xs font-bold mt-1">{isBackpackFull ? 'FULL' : `${gameData.lives}/10`}</span>
              </div>
            </div>
          </div>

          <div className="bg-black/20 h-3 rounded-full w-full relative overflow-hidden mb-1">
            <div 
              className="absolute top-0 left-0 h-full bg-amber-400 transition-all duration-1000" 
              style={{ width: `${percentage}%` }} 
            />
          </div>
          <p className="text-[10px] font-bold text-indigo-300 text-right uppercase tracking-tighter">Próximo nivel: {nextLevelXp} XP</p>
        </div>

        {/* CONTENIDO SCROLLABLE */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50 space-y-6">
          
          {/* SECCIÓN 1: RECURSOS (VIDAS) */}
          <div>
            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-3 px-2">Recursos</h3>
            <div className="bg-white p-4 rounded-3xl border-2 border-slate-100 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center text-rose-500">
                  <Heart fill="currentColor" size={24} />
                </div>
                <div>
                  <p className="font-black text-slate-700">Rellenar Vida (+1)</p>
                  <p className="text-xs text-slate-400 font-bold">{isBackpackFull ? 'Mochila llena' : `${LIFE_COST} XP`}</p>
                </div>
              </div>
              <button 
                onClick={() => { playSound('compra'); onBuyLife(LIFE_COST); }}
                disabled={isBackpackFull || userXp < LIFE_COST}
                className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-1 border-b-4 active:border-b-0 active:translate-y-1 transition-all
                  ${isBackpackFull ? 'bg-slate-100 text-slate-400 border-slate-200' : 
                    userXp >= LIFE_COST ? 'bg-green-500 text-white border-green-700' : 'bg-slate-200 text-slate-400'}`}
              >
                {isBackpackFull ? <Check size={14} /> : <><Zap size={12} fill="currentColor" /> {LIFE_COST}</>}
              </button>
            </div>
          </div>

          {/* SECCIÓN 2: COFRE SORPRESA */}
          <div>
            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-3 px-2">Skins y Sorpresas</h3>
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-6 rounded-[2rem] text-center text-white relative overflow-hidden shadow-lg">
              <div className="relative z-10">
                <Gift size={48} className="mx-auto mb-2 text-yellow-200 drop-shadow-md" />
                <h3 className="text-xl font-black mb-1">Cofre Sorpresa</h3>
                <p className="text-xs text-orange-100 font-medium mb-4">
                  {getMascotDialogue('chest_open', currentSkin, isLegendary)}
                </p>
                <button
                  onClick={() => onBuyChest()}
                  className="bg-white/20 backdrop-blur-sm border-2 border-white/40 hover:bg-white/30 text-white font-black py-3 rounded-2xl w-full flex items-center justify-center gap-2 transition-all active:scale-95"
                >
                  ABRIR x {CHEST_COST} XP
                </button>
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl translate-x-10 -translate-y-10" />
            </div>
          </div>

          {/* SECCIÓN 3: TIENDA DE SKINS */}
          <div className="grid grid-cols-2 gap-3">
            {SHOP_ITEMS.map((item) => {
              const isOwned = (userProgress.unlockedSkins || []).includes(item.id);
              const isEquipped = currentSkin === item.id;
              const itemCost = Number(item.cost || 0);
              const canAfford = userXp >= itemCost;
              const isLegendaryLocked = item.exclusive && !isOwned;

              return (
                <div key={item.id} className={`p-3 rounded-2xl border-2 flex flex-col items-center gap-2 relative transition-all bg-white
                  ${isEquipped ? 'border-green-500 ring-2 ring-green-100' : 'border-slate-100'}
                  ${isLegendaryLocked ? 'grayscale brightness-[0.4] opacity-80' : ''}
                `}>
                  <div className="relative">
                    <img src={item.img} alt={item.name} className="w-16 h-16 object-contain" />
                    {isLegendaryLocked && <div className="absolute inset-0 flex items-center justify-center text-white"><Lock size={20} /></div>}
                  </div>
                  
                  <div className="text-center w-full">
                    <p className="font-bold text-slate-700 text-[10px] leading-tight mb-1">
                      {isLegendaryLocked ? '???' : item.name}
                    </p>
                    {!isOwned && !item.exclusive && (
                      <div className={`flex items-center justify-center gap-1 font-black text-[10px] ${canAfford ? 'text-amber-600' : 'text-rose-500'}`}>
                        <Zap size={10} fill="currentColor" /> {itemCost}
                      </div>
                    )}
                    {item.exclusive && !isOwned && (
                      <p className="text-[8px] font-black text-amber-500 uppercase tracking-tighter">CONTENIDO DE COFRE</p>
                    )}
                  </div>

                  {isOwned ? (
                    <button 
                      onClick={() => onEquipSkin(item.id)}
                      disabled={isEquipped}
                      className={`w-full py-1.5 rounded-lg font-bold text-[10px] transition-all 
                        ${isEquipped ? 'bg-green-100 text-green-700' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                    >
                      {isEquipped ? 'USANDO' : 'EQUIPAR'}
                    </button>
                  ) : (
                    // ✅ BOTÓN DE COMPRA ACTUALIZADO
                    <button 
                      onClick={() => { 
                        if (item.exclusive) return; // No hacer nada si es exclusiva
                        
                        if (!canAfford) { 
                          playSound('wrong'); 
                          
                          // ✅ Mostrar error de XP con mensaje divertido
                          onInsufficientXp?.({
                            cost: itemCost,
                            current: userXp,
                            missing: itemCost - userXp,
                            itemName: item.name
                          });
                          return; 
                        } 
                        
                        // Si tiene XP, comprar
                        onBuySkin(item); 
                      }}
                      disabled={item.exclusive}  // ✅ Solo deshabilitar exclusivas
                      className={`w-full py-1.5 rounded-lg font-bold text-[10px] border-b-2 active:border-b-0 active:translate-y-0.5 transition-all
                        ${item.exclusive 
                          ? 'bg-slate-200 text-slate-400' 
                          : canAfford 
                            ? 'bg-amber-400 text-amber-900 border-amber-600 hover:bg-amber-300' 
                            : 'bg-rose-100 text-rose-400 border-rose-200 hover:bg-rose-200'}`}
                    >
                      {item.exclusive ? 'BLOQUEADA' : canAfford ? 'COMPRAR' : 'FALTA XP'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
